import { useEffect, useRef, useState } from "react";
import api, { fileUrl, errMsg, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Upload, Trash2, ChevronDown, Check, X, Clock, Plus, FileText, Mail } from "lucide-react";

// Vendor "My Account – Profile" page. Mirrors the MarketplaceSetup.jsx
// layout: single scroll of business details the vendor originally captured
// during sign-up, now editable. Saving an edit routes through the admin
// approval queue (role_profile_edits) before anything goes live.
const EMPTY_BRANCH = { name: "", address: "", city: "", province: "", phone: "", email: "" };
const EMPTY = {
  business_name: "", tagline: "", description: "", categories: [],
  location: "", phone: "", contact_email: "", website: "", social_media: "",
  logo_path: "", image_paths: [], flyer_paths: [],
  services: [],
  has_multiple_branches: false, branches: [],
};

export default function VendorProfile() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [pendingEdit, setPendingEdit] = useState(null);
  const [firstSaveDialog, setFirstSaveDialog] = useState(false);
  // Tracks whether the vendor had an existing live profile when the page
  // loaded. Used to distinguish a true first-time save (show the awaiting-
  // approval dialog + force logout) from a subsequent edit by a still-pending
  // vendor (silent save).
  const hadExistingProfile = useRef(false);
  const logoInput = useRef(null);
  const imageInput = useRef(null);
  const flyerInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/vendor/profile");
      const p = data.profile || {};
      hadExistingProfile.current = !!(p.business_name && p.business_name.trim());
      setProfile({ ...EMPTY, ...p });
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/role-profile/pending-edit");
      setPendingEdit(data.pending_edit || null);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const toggleCategory = (c) => {
    const cats = profile.categories.includes(c)
      ? profile.categories.filter((x) => x !== c)
      : [...profile.categories, c];
    setProfile({ ...profile, categories: cats });
  };

  // Media uploads post the file to object storage and then merge the
  // returned path into local state only — nothing hits the live profile
  // until the Save button triggers the admin approval flow.
  const upload = async (file, kind, onPath) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/vendor/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPath(data.path, data.filename);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    if (!profile.business_name.trim()) return toast.error("Business name is required");
    setSaving(true);
    try {
      const payload = {
        business_name: profile.business_name,
        tagline: profile.tagline,
        description: profile.description,
        categories: profile.categories,
        location: profile.location,
        phone: profile.phone,
        contact_email: profile.contact_email,
        website: profile.website,
        social_media: profile.social_media,
        services: profile.services,
        has_multiple_branches: !!profile.has_multiple_branches,
        branches: profile.has_multiple_branches ? profile.branches : [],
      };
      const { data } = await api.put("/vendor/profile", payload);
      if (data?.pending_edit) {
        toast.success("Edit submitted for admin approval");
        setPendingEdit(data.pending_edit);
      } else if (!hadExistingProfile.current && user?.vendor_status !== "approved") {
        // First-time save by a not-yet-approved vendor — surface the awaiting-
        // approval dialog, then force logout when the user acknowledges it.
        hadExistingProfile.current = true;
        setFirstSaveDialog(true);
      } else {
        toast.success("Profile saved!");
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="vendor-profile">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><Store className="w-5 h-5 text-white" /></span>
        <div>
          <h1 className="text-2xl font-black text-forest">My account — Vendor profile</h1>
          <p className="text-sm text-forest/60">Update your business details. Changes are reviewed before going live.</p>
        </div>
      </div>

      {pendingEdit && (
        <div className="bg-amber-100/70 border border-amber-300/50 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="vp-pending-banner">
          <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-forest">Changes awaiting admin review</p>
            <p className="text-sm text-forest/70">Submitted {new Date(pendingEdit.created_at).toLocaleString()}. You'll be emailed once an admin approves them.</p>
          </div>
        </div>
      )}

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-4">
        <div>
          <Label>Business logo</Label>
          <div className="mt-2 flex items-center gap-4">
            {profile.logo_path ? (
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                <img src={fileUrl(profile.logo_path)} alt="Business logo" className="w-full h-full object-cover" data-testid="vp-logo-preview" />
                <button type="button" onClick={() => setProfile({ ...profile, logo_path: "" })}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove logo" data-testid="vp-logo-remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                <Upload className="w-6 h-6" />
              </div>
            )}
            <div>
              <input ref={logoInput} type="file" accept="image/*" className="hidden"
                onChange={(e) => upload(e.target.files?.[0], "logo", (path) => setProfile((p) => ({ ...p, logo_path: path })))}
                data-testid="vp-logo-input" />
              <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
                onClick={() => logoInput.current?.click()} disabled={uploading === "logo"} data-testid="vp-logo-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading === "logo" ? "Uploading..." : (profile.logo_path ? "Replace logo" : "Upload logo")}
              </Button>
              <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB.</p>
            </div>
          </div>
        </div>

        <div>
          <Label>Business name *</Label>
          <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className="mt-1" data-testid="vp-business-name" />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} className="mt-1" placeholder="One-line pitch" data-testid="vp-tagline" />
        </div>
        <div>
          <Label>About your business</Label>
          <Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={4} className="mt-1" placeholder="Tell families what you offer, ages served, approach..." data-testid="vp-description" />
        </div>

        <div>
          <Label>Categories</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="vp-categories">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => toggleCategory(c)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${profile.categories.includes(c) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                data-testid={`vp-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Location / service area</Label>
            <Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} className="mt-1" data-testid="vp-location" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1" data-testid="vp-phone" />
          </div>
          <div>
            <Label>Public contact email</Label>
            <Input value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} className="mt-1" data-testid="vp-contact-email" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} className="mt-1" placeholder="https://..." data-testid="vp-website" />
          </div>
          <div className="sm:col-span-2">
            <Label>Social media</Label>
            <Input value={profile.social_media} onChange={(e) => setProfile({ ...profile, social_media: e.target.value })} className="mt-1" placeholder="Instagram, Facebook or TikTok handle / link" data-testid="vp-social-media" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Services</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setProfile({ ...profile, services: [...profile.services, { name: "", description: "", price: "" }] })} data-testid="vp-add-service-btn">
              <Plus className="w-4 h-4 mr-1" /> Add service
            </Button>
          </div>
          <div className="space-y-3" data-testid="vp-services-list">
            {profile.services.map((s, i) => (
              <div key={i} className="bg-softstone/60 rounded-2xl p-4 grid sm:grid-cols-[1fr_1fr_140px_40px] gap-3 items-start">
                <Input value={s.name} onChange={(e) => { const arr = [...profile.services]; arr[i] = { ...s, name: e.target.value }; setProfile({ ...profile, services: arr }); }}
                  placeholder="Service name" className="rounded-xl bg-white border-sage/30" data-testid={`vp-service-name-${i}`} />
                <Input value={s.description} onChange={(e) => { const arr = [...profile.services]; arr[i] = { ...s, description: e.target.value }; setProfile({ ...profile, services: arr }); }}
                  placeholder="Short description" className="rounded-xl bg-white border-sage/30" data-testid={`vp-service-desc-${i}`} />
                <Input value={s.price} onChange={(e) => { const arr = [...profile.services]; arr[i] = { ...s, price: e.target.value }; setProfile({ ...profile, services: arr }); }}
                  placeholder="R price" className="rounded-xl bg-white border-sage/30" data-testid={`vp-service-price-${i}`} />
                <button type="button" onClick={() => setProfile({ ...profile, services: profile.services.filter((_, j) => j !== i) })}
                  className="p-2 text-terracotta-dark hover:bg-terracotta-light rounded-xl" data-testid={`vp-service-remove-${i}`} aria-label="Remove service">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {profile.services.length === 0 && <p className="text-xs text-forest/50">No services yet — add your first one above.</p>}
          </div>
        </div>

        <div>
          <Label>Do you have multiple branches?</Label>
          <div className="mt-2 flex gap-2" data-testid="vp-multi-branch-toggle">
            {[
              { v: false, label: "No" },
              { v: true, label: "Yes" },
            ].map((o) => (
              <button
                key={o.label}
                type="button"
                onClick={() => setProfile({
                  ...profile,
                  has_multiple_branches: o.v,
                  branches: o.v ? (profile.branches.length ? profile.branches : [{ ...EMPTY_BRANCH }]) : [],
                })}
                className={`text-xs font-bold px-4 py-2 rounded-full border transition-colors ${profile.has_multiple_branches === o.v ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                data-testid={`vp-multi-branch-${o.label.toLowerCase()}`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {profile.has_multiple_branches && (
            <div className="mt-4 space-y-4">
              <div>
                <Label>How many branches?</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={profile.branches.length || ""}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(20, parseInt(e.target.value) || 0));
                    const arr = Array.from({ length: n }, (_, i) => profile.branches[i] || { ...EMPTY_BRANCH });
                    setProfile({ ...profile, branches: arr });
                  }}
                  className="mt-1 w-32"
                  data-testid="vp-branch-count"
                />
              </div>

              <div className="space-y-4" data-testid="vp-branches-list">
                {profile.branches.map((b, i) => (
                  <div key={i} className="bg-softstone/60 rounded-2xl p-4 space-y-3" data-testid={`vp-branch-${i}`}>
                    <p className="text-sm font-bold text-forest">Branch {i + 1}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Branch name</Label>
                        <Input
                          value={b.name}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, name: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-name-${i}`}
                        />
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={b.address}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, address: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-address-${i}`}
                        />
                      </div>
                      <div>
                        <Label>City</Label>
                        <Input
                          value={b.city}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, city: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-city-${i}`}
                        />
                      </div>
                      <div>
                        <Label>Province</Label>
                        <Input
                          value={b.province}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, province: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-province-${i}`}
                        />
                      </div>
                      <div>
                        <Label>Phone number</Label>
                        <Input
                          value={b.phone}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, phone: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-phone-${i}`}
                        />
                      </div>
                      <div>
                        <Label>Branch email</Label>
                        <Input
                          type="email"
                          value={b.email}
                          onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, email: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                          className="mt-1 rounded-xl bg-white border-sage/30"
                          data-testid={`vp-branch-email-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-sage hover:bg-sage-dark text-white w-full" data-testid="vp-save-btn">
          {saving ? "Saving..." : (user?.vendor_status === "approved" ? "Submit for approval" : "Save profile")}
        </Button>
      </div>

      <Dialog
        open={firstSaveDialog}
        onOpenChange={(open) => {
          if (!open) {
            setFirstSaveDialog(false);
            logout();
          }
        }}
      >
        <DialogContent data-testid="vp-first-save-dialog" className="rounded-3xl">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <Mail className="w-6 h-6 text-amber-700" />
            </div>
            <DialogTitle className="text-xl font-black text-forest" data-testid="vp-first-save-dialog-title">
              Profile submitted — awaiting admin approval
            </DialogTitle>
            <DialogDescription className="text-sm text-forest/70 leading-relaxed" data-testid="vp-first-save-dialog-body">
              Thanks for setting up your vendor profile! Our team will review it shortly.
              Please log out and wait for the approval email — once it lands, you can sign
              back in and continue to payment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setFirstSaveDialog(false);
                logout();
              }}
              className="rounded-full bg-sage hover:bg-sage-dark text-white w-full"
              data-testid="vp-first-save-dialog-logout-btn"
            >
              Got it — log me out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
